import JSZip from "jszip";
import { saveAs } from "file-saver";

/**
 * Build image blob from URL (canvas + CORS; falls back to XHR / proxy like client Photos tab).
 */
function getImageBlobFromUrlViaBrowser(imageUrl) {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = function () {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(
        function (blob) {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Failed to convert image to blob"));
          }
        },
        "image/jpeg",
        0.95,
      );
    };

    img.onerror = function () {
      const directDownload = new XMLHttpRequest();
      directDownload.open("GET", imageUrl, true);
      directDownload.responseType = "blob";

      directDownload.onload = function () {
        if (this.status === 200) {
          resolve(this.response);
        } else {
          reject(new Error(`Failed to load image directly: ${this.status}`));
        }
      };

      directDownload.onerror = function () {
        const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(imageUrl)}`;
        const proxyDownload = new XMLHttpRequest();
        proxyDownload.open("GET", proxyUrl, true);
        proxyDownload.responseType = "blob";

        proxyDownload.onload = function () {
          if (this.status === 200) {
            resolve(this.response);
          } else {
            reject(new Error(`All download methods failed for image`));
          }
        };

        proxyDownload.onerror = function () {
          reject(new Error(`All download methods failed for image`));
        };

        proxyDownload.send();
      };

      directDownload.send();
    };

    img.src = `${imageUrl}${imageUrl.includes("?") ? "&" : "?"}cb=${new Date().getTime()}`;
  });
}

/**
 * Zip progress photos by date folder (same layout as client Photos tab).
 *
 * @param {object} opts
 * @param {Array<{ url: string, displayName?: string, type: string }>} opts.photos - flat list
 * @param {Array<{ date: Date|string, photos: Array }>} opts.groupedPhotos
 * @param {string} opts.clientLabel - safe folder / zip name fragment
 * @param {(n: number) => void} [opts.onProgress] - 0–100
 * @returns {Promise<void>}
 */
export async function downloadProgressPhotosZip({
  photos,
  groupedPhotos,
  clientLabel,
  onProgress,
}) {
  if (!photos?.length || !groupedPhotos?.length) {
    throw new Error("No photos to download");
  }

  const zip = new JSZip();
  const safeName = (clientLabel || "client").replace(/[^\w\-]+/g, "_");

  zip.file(
    "README.txt",
    `Progress Photos for ${safeName}\nGenerated: ${new Date().toISOString().split("T")[0]}\n\n` +
      `Folders are named by submission date (YYYY-MM-DD).\n`,
  );

  const totalPhotos = photos.length;
  let processedPhotos = 0;
  const report = (partial) => {
    if (onProgress) {
      onProgress(Math.round((partial / totalPhotos) * 100));
    }
  };

  for (const group of groupedPhotos) {
    const groupDate = new Date(group.date);
    const folderName = groupDate.toISOString().split("T")[0];
    const folder = zip.folder(folderName);

    if (!folder) continue;

    for (const photo of group.photos) {
      try {
        const imageBlob = await getImageBlobFromUrlViaBrowser(photo.url);
        const photoType = photo.displayName || photo.type;
        const fileName = `${String(photoType).replace(/\s+/g, "_")}.jpg`;
        folder.file(fileName, imageBlob);
        processedPhotos++;
        report(processedPhotos);
      } catch (e) {
        console.error("zip photo error:", e?.message, photo?.url);
      }
    }
  }

  let fileCount = 0;
  zip.forEach(() => {
    fileCount++;
  });

  if (fileCount <= 1) {
    throw new Error("Could not add image files to the zip (see console).");
  }

  const content = await zip.generateAsync(
    {
      type: "blob",
      compression: "DEFLATE",
      compressionOptions: { level: 6 },
    },
    (metadata) => {
      if (onProgress) {
        onProgress(Math.round(metadata.percent));
      }
    },
  );

  const today = new Date().toISOString().split("T")[0];
  saveAs(content, `${safeName}_progress_photos_${today}.zip`);
}
